import React from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/avatar'
import { cn } from '@repo/ui/utils'
import { Typography } from '@repo/ui/typography'

export interface OrganizationBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  organization: {
    id?: string
    name?: string
    logoUrl?: string
  }
  avatarClassName?: string
  variant?: 'default' | 'small'
}

export const OrganizationBadge = React.forwardRef<HTMLDivElement, OrganizationBadgeProps>(
  ({ organization, className, avatarClassName, variant = 'default', ...props }, ref) => {
    const isSmallVariant = variant === 'small'
    const avatarSize = isSmallVariant ? 'w-5 h-5' : 'w-6 h-6'
    const avatarClassNameFinal = cn(avatarSize, avatarClassName)

    // Common avatar component
    const AvatarComponent = (
      <Avatar className={avatarClassNameFinal}>
        <AvatarImage src={organization.logoUrl ?? ''} />
        <AvatarFallback className="bg-gradient-to-b from-orange-400/50 to-gray-500/50" />
      </Avatar>
    )

    // Common text content
    const textContent = organization.name ?? 'AminoChain Provider'

    if (isSmallVariant) {
      return (
        <div
          className={cn(
            'inline-flex items-center rounded-xl border font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-white border-border p-1 pr-3 gap-2',
            className
          )}
          ref={ref}
          {...props}
        >
          {AvatarComponent}
          <Typography weight="medium" className="text-xs">
            {textContent}
          </Typography>
        </div>
      )
    }

    return (
      <div
        className={cn(
          'flex items-center text-black text-base rounded-3xl gap-2 p-2 pr-5 border border-border',
          className
        )}
        ref={ref}
        {...props}
      >
        {AvatarComponent}
        <Typography weight="regular">{textContent}</Typography>
      </div>
    )
  }
)

OrganizationBadge.displayName = 'OrganizationBadge'
